<template>
  <div class="transaction-selection" style="height: 100vh; overflow-y: hidden; overflow-x: hidden;">
    <div class="transaction-selection-header-and-priority-section">
      <h2 class="section-title">Select Transaction Type</h2>

      <!-- Priority Mode Toggle -->
      <div class="priority-section" style="margin-bottom: 1rem;">
        <div class="priority-mode-card" :class="{ 'priority-active': isPriority }">
          <div class="d-flex justify-content-between align-items-center">
            <div class="priority-label">
              <i class="fas fa-star me-2 text-warning"></i>
              <span>Enable Priority Mode</span>
            </div>
            <div class="form-check form-switch">
              <input 
                class="form-check-input priority-switch" 
                type="checkbox" 
                id="priority-switch"
                v-model="isPriority"
                style="width: 3rem; height: 1.5rem;"
              >
              <label class="form-check-label priority-status ms-2" for="priority-switch">
                {{ isPriority ? 'ON' : 'OFF' }}
              </label>
            </div>
          </div>
          
          <div v-if="isPriority" class="priority-code-section mt-3">
            <div class="input-group">
              <span class="input-group-text">
                <i class="fas fa-key"></i>
              </span>
              <input
                type="password"
                class="form-control form-control-lg"
                placeholder="Enter 4-digit priority code"
                v-model="priorityCode"
                maxlength="4"
                @input="autoVerifyCode"
              />
            </div>
            
            <!-- Keyboard toggle button -->
            <button 
              v-if="!priorityVerified" 
              class="btn btn-outline-secondary mt-2"
              @click="toggleKeyboard"
              style="background-color: var(--brand-dark); color: #ffffff;"
            >
              <i class="fas" :class="keyboardVisible ? 'fa-keyboard' : 'fa-keyboard'"></i>
              {{ keyboardVisible ? 'Hide Keyboard' : 'Show Keyboard' }}
            </button>
            
            <!-- Keyboard component -->
            <SimpleKeyboard
              v-if="keyboardVisible && !priorityVerified"
              @onChange="onChange"
              @onKeyPress="onKeyPress"
              :input="priorityCode"
            />

            <div v-if="priorityCode.length === 4 && priorityVerified" class="alert alert-success mt-3">
              <i class="fas fa-check-circle me-2"></i>Priority code verified
            </div>
            <div v-else-if="priorityCode.length === 4 && !priorityVerified" class="alert alert-danger mt-3">
              <i class="fas fa-exclamation-triangle me-2"></i>Invalid priority code
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Priority Notice Banner (when verified) -->
    <div v-if="isPriority && priorityVerified" class="priority-notice mb-4">
      <i class="fas fa-star me-2"></i> Priority Transaction
    </div>
    
    <div v-if="loading" class="loading-container">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p>Loading transaction types...</p>
    </div>
    
    <div v-else-if="error" class="error-container">
      <div class="alert alert-danger">
        {{ error }}
      </div>
      <button class="btn btn-primary" @click="loadTransactionTypes">
        Try Again
      </button>
    </div>
    
    <div v-else-if="transactionTypes.length === 0" class="no-transactions">
      <div class="alert alert-info">
        No transaction types are currently available.
        <br>Please check back later.
      </div>
    </div>
    
    <div v-else class="transaction-grid" id="transaction-grid"
      :class="{ 'single-item': transactionTypes.length === 1, 'two-items': transactionTypes.length === 2 }"
      :style="gridContainerStyle"
    >
      <div 
        v-for="(type, index) in transactionTypes" 
        :key="type.id" 
        class="transaction-card"
        :class="{ 'wide-card': isLastInOddRow(index, transactionTypes.length) }"
        @click="selectTransactionType(type)"
      >
        <div class="card">
          <div class="card-body">
            <h5 class="card-title">{{ type.display_name }}</h5>
            <p class="card-text" v-if="type.description">{{ type.description }}</p>
            <div class="transaction-icon">
              <i :class="getIconClass(type)"></i>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <p v-if="transactionTypes.length > 2" class="scroll-hint">Scroll down to see more</p>

    <!-- Requirements Modal -->
    <div 
      class="modal fade" 
      id="requirementsModal" 
      tabindex="-1" 
      aria-labelledby="requirementsModalLabel" 
      aria-hidden="true"
    >
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="requirementsModalLabel">Transaction Requirements</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <p v-if="selectedType" class="lead">
              Please ensure you have the following requirements for <strong>{{ selectedType.display_name }}</strong>:
            </p>
            
            <div class="requirements-list" v-if="selectedType && selectedType.requirements">
              <!-- Select All Checkbox -->
              <label v-if="selectedType.enable_select_all" class="select-all-container mb-3" for="select-all">
                <div class="form-check">
                  <input 
                    class="form-check-input" 
                    type="checkbox" 
                    id="select-all"
                    v-model="allSelected"
                    @change="toggleAllRequirements"
                  />
                  <span class="form-check-label">
                    Select All Requirements
                  </span>
                </div>
              </label>
              
              <div 
                v-for="(req, index) in selectedType.requirements" 
                :key="index" 
                class="requirement-item form-check"
              >
                <input 
                  class="form-check-input" 
                  type="checkbox" 
                  :id="'req-' + index"
                  v-model="checkedRequirements[index]"
                  @change="updateSelectAllState"
                />
                <label class="form-check-label" :for="'req-' + index">
                  {{ req.description }}
                </label>
              </div>
            </div>
            
            <div v-else class="no-requirements">
              <p>No specific requirements for this transaction.</p>
            </div>
          </div>
          <div class="modal-footer flex-column align-items-stretch">
            <div class="d-flex justify-content-center w-100">
              <button 
                type="button" 
                class="btn btn-primary btn-lg w-100"
                @click="checkRequirementsAndProceed"
              >
                Proceed
              </button>
            </div>
            <!-- Requirements notice -->
            <div v-if="showRequirementsNotice" class="requirements-notice mt-3 text-danger text-center w-100">
              <i class="fas fa-exclamation-circle me-2"></i>
              Please check all the completed requirements before proceeding
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Service Unavailable Modal -->
    <div 
      class="modal fade" 
      id="serviceUnavailableModal" 
      tabindex="-1" 
      aria-labelledby="serviceUnavailableModalLabel" 
      aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="serviceUnavailableModalLabel">Service Unavailable</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body text-center">
            <i class="fas fa-clock fa-4x text-warning mb-3"></i>
            <h4>Service will be available soon!</h4>
            <p class="mt-3">We're currently working on implementing this service. Please check back later.</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-primary" data-bs-dismiss="modal">OK</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { Modal } from 'bootstrap';
import { priorityCodesApi } from '@/services/api';
import SimpleKeyboard from "@/components/SimpleKeyboard.vue";

export default {
  name: 'TransactionSelection',
  components: { SimpleKeyboard },
  props: {
    config: {
      type: Object,
      default: () => ({
        categorized: true,
        showPopular: true
      })
    },
    transactionData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      requirementsModal: null,
      serviceUnavailableModal: null,
      checkedRequirements: {},
      selectedType: null,
      isPriority: false,
      priorityCode: '',
      priorityVerified: false,
      priorityVerificationAttempted: false,
      allSelected: false,
      verificationTimeout: null,
      showRequirementsNotice: false,
      keyboardVisible: false  // Controls keyboard visibility
    };
  },
  computed: {
    ...mapGetters({
      transactionTypes: 'kiosk/getTransactionTypes',
      loading: 'kiosk/isLoading',
      error: 'kiosk/getError',
      storePriorityMode: 'kiosk/isPriorityMode',
      storePriorityCode: 'kiosk/getPriorityCode'
    }),
    allRequirementsMet() {
      // If there are no requirements, consider them met
      if (!this.selectedType || !this.selectedType.requirements || this.selectedType.requirements.length === 0) {
        return true;
      }
      
      // Otherwise check if all requirements are checked
      return this.selectedType.requirements.every((_, index) => this.checkedRequirements[index]);
    },
    gridContainerStyle() {
      const totalItems = this.transactionTypes.length;
      const isSingle = totalItems === 1;
      const box = {
        boxShadow: '0 0 10px 0 rgba(50, 49, 47, 0.08)',
        borderRadius: '10px',
        padding: '1rem'
      };

      if (isSingle) {
        return {
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr)',
          gridTemplateRows: 'minmax(0, 1fr)',
          placeItems: 'center',
          height: 'auto',
          minHeight: this.isPriority ? '44vh' : '50vh',
          overflowY: 'auto',
          ...box
        };
      }

      if (totalItems === 2) {
        return {
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gridTemplateRows: 'auto',
          gridAutoRows: 'minmax(240px, 300px)',
          alignContent: 'start',
          gap: '1.5rem',
          height: 'auto',
          minHeight: '240px',
          overflowY: 'auto',
          ...box
        };
      }

      return {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gridTemplateRows: 'auto',
        gridAutoRows: 'minmax(240px, 300px)',
        alignContent: 'start',
        gap: '1.5rem',
        height: this.isPriority ? '62vh' : '66vh',
        overflowY: 'auto',
        ...box
      };
    }
  },
  async created() {
    await this.loadTransactionTypes();
    this.loadPriorityState();
  },
  mounted() {
    this.requirementsModal = new Modal(document.getElementById('requirementsModal'));
    this.serviceUnavailableModal = new Modal(document.getElementById('serviceUnavailableModal'));
  },
  methods: {
    ...mapActions({
      fetchTransactionTypes: 'kiosk/fetchTransactionTypes',
      storeSelectTransactionType: 'kiosk/selectTransactionType',
      setRequirementsMet: 'kiosk/setRequirementsMet',
      setPriorityMode: 'kiosk/setPriorityMode'
    }),
    isLastInOddRow(index, totalItems) {
      return totalItems > 2 && totalItems % 2 === 1 && index === totalItems - 1;
    },
    async loadTransactionTypes() {
      try {
        await this.fetchTransactionTypes();
      } catch (error) {
        console.error('Failed to load transaction types:', error);
      }
    },
    loadPriorityState() {
      // Load priority state from store or sessionStorage
      const savedPriority = sessionStorage.getItem('kiosk_priority_enabled');
      const savedCode = sessionStorage.getItem('kiosk_priority_code');
      const savedVerified = sessionStorage.getItem('kiosk_priority_verified');
      
      if (savedPriority === 'true' && savedCode && savedVerified === 'true') {
        this.isPriority = true;
        this.priorityCode = savedCode;
        this.priorityVerified = true;
        
        // Update store with saved priority state
        this.updatePriorityInStore();
      } else if (this.storePriorityMode && this.storePriorityCode) {
        // If store has priority data but session storage doesn't, use store data
        this.isPriority = this.storePriorityMode;
        this.priorityCode = this.storePriorityCode;
        this.priorityVerified = true;
        
        // Save to session storage
        this.savePriorityState();
      }
    },
    savePriorityState() {
      sessionStorage.setItem('kiosk_priority_enabled', this.isPriority ? 'true' : 'false');
      sessionStorage.setItem('kiosk_priority_code', this.priorityCode || '');
      sessionStorage.setItem('kiosk_priority_verified', this.priorityVerified ? 'true' : 'false');
    },
    getIconClass(type) {
      // Map transaction types to Font Awesome icons
      const iconMap = {
        'registration': 'fas fa-clipboard-list',
        'admission': 'fas fa-user-plus',
        'payment': 'fas fa-money-bill-wave',
        'grades': 'fas fa-graduation-cap',
        'id': 'fas fa-id-card',
        'certificate': 'fas fa-certificate',
        'scholarship': 'fas fa-award'
      };
      
      // Try to match by code or fallback to default
      return iconMap[type.code?.toLowerCase()] || 'fas fa-tasks';
    },
    async selectTransactionType(type) {
      this.selectedType = type;
      
      // Clear any existing student data when selecting a new transaction type
      this.$store.commit('kiosk/SET_STUDENT', null);
      
      // Check if this is Tuition Payment or Enrollment (OLD STUDENTS)
      if (type.display_name === "Currently Allowing All Transactions") {
        // Show the service unavailable modal for other transactions
        this.serviceUnavailableModal.show();
        return; // Stop processing further
      }
      
      // Special handling for guest/anonymous transaction types (no student ID required)
      if (type.requires_student === false) {
        try {
          const isNewStudentAdmission = type.display_name === "Entrance Exam Payment";
          
          if (isNewStudentAdmission) {
            // Only set priority for New Student Admission
            const priorityCode = '1234';
            // Directly set priority in the store
            this.$store.commit('kiosk/SET_PRIORITY_MODE', true);
            this.$store.commit('kiosk/SET_PRIORITY_CODE', priorityCode);
            this.$store.commit('kiosk/SET_PRIORITY_VERIFIED', true);
            // Set local state to match
            this.isPriority = true;
            this.priorityCode = priorityCode;
            this.priorityVerified = true;
          } 
          // For other guest types, don't modify priority state - let user decide
          
          // Select transaction type in store
          await this.storeSelectTransactionType(type);
          
          // Set requirements as met for guest transactions
          await this.setRequirementsMet(true);
          
          // Emit step-complete with null student ID for guest transactions
          this.$emit('step-complete', {
            transactionType: type,
            isPriority: isNewStudentAdmission, // Only true for New Student Admission
            studentId: null,
            skipVerification: true
          });
          
          return; // Exit early after handling guest transaction
        } catch (error) {
          console.error(`Error processing ${type.display_name}:`, error);
          this.$store.commit('kiosk/SET_ERROR', `Failed to process ${type.display_name}. Please try again.`);
          return;
        }
      }
      
      // Original logic for other transaction types
      this.checkedRequirements = {};
      
      // Reset requirements checkboxes
      if (type.requirements) {
        type.requirements.forEach((_, index) => {
          this.checkedRequirements[index] = false;
        });
      }
      
      // Select transaction type in store
      await this.storeSelectTransactionType(type);
      
      // Show requirements modal
      this.requirementsModal.show();
    },
    async autoVerifyCode() {
      // Clear any existing timeout
      if (this.verificationTimeout) {
        clearTimeout(this.verificationTimeout);
      }
      
      // Reset verification status if code changes
      this.priorityVerified = false;
      
      // Only verify if code is exactly 4 characters
      if (this.priorityCode.length === 4) {
        // Add a small delay to allow for typing to complete
        this.verificationTimeout = setTimeout(async () => {
          try {
            this.priorityVerificationAttempted = true;
            
            // Call the API service to verify the priority code
            const response = await priorityCodesApi.verifyPriorityCode(this.priorityCode);
            
            if (response.data.valid) {
              this.priorityVerified = true;
              // Update the store with the verified code
              this.updatePriorityInStore();
              this.savePriorityState();
            } else {
              this.priorityVerified = false;
              this.updatePriorityInStore();
              this.savePriorityState();
            }
          } catch (error) {
            console.error('Error verifying priority code:', error);
            this.priorityVerified = false;
            this.updatePriorityInStore();
            this.savePriorityState();
          }
        }, 500);
      } else {
        // Reset verification status for incomplete codes
        this.priorityVerificationAttempted = false;
        
        // Update store to disable priority mode if code is incomplete
        this.updatePriorityInStore();
        this.savePriorityState();
      }
    },
    updatePriorityInStore() {
      // Update priority mode in store
      this.setPriorityMode({
        isPriority: this.isPriority && this.priorityVerified,
        priorityCode: (this.isPriority && this.priorityVerified) ? this.priorityCode : ''
      });
    },
    async checkRequirementsAndProceed() {
      // Consider it requirements met when no requirements exist
      const hasNoRequirements = !this.selectedType || 
                               !this.selectedType.requirements || 
                               this.selectedType.requirements.length === 0;
                               
      if (hasNoRequirements || this.allRequirementsMet) {
        try {
          // Hide notice if it was previously shown
          this.showRequirementsNotice = false;
          
          // Set requirements met in store
          await this.setRequirementsMet(true);
          
          // Update priority in store to ensure it's current
          this.updatePriorityInStore();
          
          // Move focus away from the button before hiding the modal
          document.body.focus();
          
          // Hide modal
          this.requirementsModal.hide();
          
          // Store the selected transaction type in Vuex store
          await this.storeSelectTransactionType(this.selectedType);
          
          // Get the latest priority state from the store
          const isPriority = this.isPriority && this.priorityVerified;
          
          // Use step-complete event instead of router navigation for the orchestrator
          this.$emit('step-complete', {
            transactionType: this.selectedType,
            isPriority: isPriority,
            priorityCode: isPriority ? this.priorityCode : null
          });
        } catch (error) {
          console.error('Error in checkRequirementsAndProceed:', error);
          this.$store.commit('kiosk/SET_ERROR', 'Failed to process transaction. Please try again.');
        }
      } else {
        // Show notice when requirements aren't met
        this.showRequirementsNotice = true;
      }
    },
    toggleAllRequirements() {
      if (this.selectedType && this.selectedType.requirements) {
        this.selectedType.requirements.forEach((_, index) => {
          this.checkedRequirements[index] = this.allSelected;
        });
      }
    },
    updateSelectAllState() {
      if (this.selectedType && this.selectedType.requirements) {
        this.allSelected = this.selectedType.requirements.every((_, index) => this.checkedRequirements[index]);
      }
    },
    toggleKeyboard() {
      this.keyboardVisible = !this.keyboardVisible;
    },
    onChange(input) {
      this.priorityCode = input;
      this.autoVerifyCode();
    },
    onKeyPress(button) {
      console.log("Button pressed:", button);
    }
  },
  watch: {
    isPriority(newValue) {
      if (!newValue) {
        // Reset when priority mode is turned off
        this.priorityCode = '';
        this.priorityVerified = false;
        this.priorityVerificationAttempted = false;
        this.keyboardVisible = false; // Hide keyboard when priority mode is off
        this.updatePriorityInStore();
        this.savePriorityState();
      }
    },
    priorityVerified(newValue) {
      if (newValue) {
        this.keyboardVisible = false; // Hide keyboard when verified
      }
    }
  }
};
</script>

<style scoped>
.transaction-selection {
  padding: 2rem 1.5rem 1rem;
  max-width: 1200px;
  margin: 0 auto;
  font-family: var(--font-body);
}

.section-title {
  text-align: center;
  margin-top: 0.5rem;
  margin-bottom: 2.5rem;
  font-family: var(--font-heading);
  font-weight: 700;
  color: var(--brand-dark);
  font-size: 2.2rem;
}

.loading-container,
.error-container,
.no-transactions {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.transaction-grid {
  display: grid;
  width: 100%;
  max-width: 960px;
  margin: 0 auto 2rem;
  align-content: start;
}

.transaction-grid.single-item {
  max-width: none;
}

.transaction-grid.single-item .card {
  height: auto;
  width: min(620px, 100%);
  min-height: 320px;
}

.transaction-grid.two-items {
  margin-top: 1rem;
}

.transaction-card {
  cursor: pointer;
  transition: transform 0.3s ease;
}

.transaction-card.wide-card {
  grid-column: 1 / -1;
}

.transaction-card:hover {
  transform: translateY(-5px);
}

.transaction-card .card {
  height: 100%;
  border: 2px solid var(--brand-border);
  border-radius: 12px;
  box-shadow: 0 4px 10px rgba(50, 49, 47, 0.08);
  transition: box-shadow 0.3s ease, border-color 0.3s ease;
  display: flex;
  flex-direction: column;
}

.transaction-card .card:hover {
  box-shadow: 0 10px 22px rgba(24, 149, 167, 0.18);
  border-color: var(--brand-primary);
}

.transaction-card .card-body {
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  padding: 1.5rem;
}

.transaction-card .card-title {
  font-family: var(--font-heading);
  font-size: 1.9rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  text-align: center;
  color: var(--brand-dark);
}

.transaction-card .card-text {
  color: var(--secondary-text);
  text-align: center;
}

.transaction-icon {
  font-size: 2.5rem;
  margin-top: 1rem;
  text-align: center;
  color: var(--brand-primary);
}

.scroll-hint {
  color: var(--secondary-text);
  text-align: center;
  margin: -1rem 0 1.5rem;
  font-size: 0.95rem;
}

.priority-section {
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
}

.priority-notice {
  background-color: var(--brand-accent);
  color: var(--brand-dark);
  padding: 0.5rem 1.6rem;
  border-radius: 8px;
  width: auto;
  min-width: 260px;
  text-align: center;
  font-size: 1.05rem;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(255, 200, 34, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  margin-right: auto;
}

.priority-mode-card {
  width: 100%;
  padding: 0.85rem 1.5rem;
  background-color: var(--brand-dark);
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(50, 49, 47, 0.14);
  border-left: 5px solid var(--brand-accent);
  transition: all 0.3s ease;
}

.priority-mode-card.priority-active {
  background-color: var(--brand-primary);
  border-left-color: var(--brand-accent);
  box-shadow: 0 4px 12px rgba(24, 149, 167, 0.3);
}

.priority-label {
  font-family: var(--font-heading);
  font-size: 1.1rem;
  font-weight: 600;
  color: #ffffff;
}

.priority-code-section {
  margin-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  padding-top: 1rem;
}

.form-check-input:checked {
  background-color: var(--brand-primary);
  border-color: var(--brand-primary);
}

.requirements-list {
  margin-top: 1rem;
}

.requirement-item {
  margin-bottom: 0.75rem;
  font-size: 1.1rem;
  color: var(--brand-dark);
}

.select-all-container {
  display: block;
  padding: 0.75rem 1rem;
  background-color: var(--brand-surface);
  border: 1px solid var(--brand-border);
  border-radius: 0.5rem;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.select-all-container:hover {
  background-color: var(--brand-ice);
  border-color: var(--brand-cyan);
}

.select-all-container .form-check {
  margin-bottom: 0;
}

.select-all-container .form-check-label {
  font-weight: 500;
  color: var(--brand-dark);
}

.priority-switch:checked {
  background-color: var(--brand-accent);
  border-color: var(--brand-accent);
}

.priority-status {
  font-weight: 500;
  font-size: 0.9rem;
  width: 30px;
  color: #ffffff;
}

.btn-primary {
  background-color: var(--brand-primary);
  border-color: var(--brand-primary);
  border-radius: 12px;
}

.btn-primary:hover {
  background-color: var(--brand-cyan);
  border-color: var(--brand-cyan);
}

.requirements-notice {
  font-weight: 500;
  animation: fadeIn 0.3s ease;
}

/* Portrait mode adjustments */
@media screen and (orientation: portrait) {
  .section-title {
    font-size: 2.4rem;
    margin-bottom: 2.5rem;
  }

  .transaction-card .card-title {
    font-size: 1.6rem;
  }

  .transaction-card .card-text {
    font-size: 1.2rem;
    text-align: center;
  }

  .transaction-icon {
    font-size: 3rem;
  }

  .transaction-grid.single-item .card {
    min-height: 380px;
    width: min(720px, 100%);
  }

  .priority-notice {
    font-size: 1.4rem;
    padding: 0.75rem 2rem;
    border-radius: 10px;
    margin-bottom: 2rem;
  }

  .requirement-item {
    font-size: 1.3rem;
    margin-bottom: 1rem;
  }

  .select-all-container {
    padding: 1rem 1.25rem;
  }

  .select-all-container .form-check-label {
    font-size: 1.3rem;
  }

  .priority-label {
    font-size: 1.3rem;
  }

  .priority-mode-card {
    padding: 1.25rem 2rem;
  }
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}
</style> 